<template>
  <div>
    <!-- Charts Section -->
    <BuyerBillingCharts
      v-if="accountType === 'buyer' && payments && payments.invoices && payments.invoices.length > 0"
      :invoices="payments.invoices"
      :enable-fake-data="enableFakeChartData"
    />

    <CCard>
      <CCardHeader>
        <div class="d-flex align-items-center mb-3">
          <CSwitch
            variant="3d"
            color="primary"
            horizontal
            size="sm"
            class="mr-2"
            :checked.sync="showActiveOnly"
          />
          <h5 class="m-1">
            Show Pending Only
          </h5>
        </div>
        <CRow>
          <CCol
            col="6"
            lg="3"
            class="px-2 mb-2"
          >
            <CWidgetSimple
              header="Outstanding"
              :text="formatAmount(outstandingAmount)"
            >
              <small class="text-muted">{{ outstandingCount }} Invoices</small>
            </CWidgetSimple>
          </CCol>
          <CCol
            col="6"
            lg="3"
            class="px-2 mb-2"
          >
            <CWidgetSimple
              header="Overdue"
              :text="formatAmount(overdueAmount)"
            >
              <small class="text-muted">{{ overdueCount }} Invoices</small>
            </CWidgetSimple>
          </CCol>
          <CCol
            col="6"
            lg="3"
            class="px-2 mb-2"
          >
            <CWidgetSimple
              header="Nearest Payout"
              :text="formatDateInclusive(nearestPayoutInvoice.dateDueRaw)"
            >
              <CButton
                v-if="nearestPayoutInvoice.invoiceId"
                size="sm"
                color="link"
                class="p-0"
                @click="getDownloadLink(nearestPayoutInvoice.accountId, nearestPayoutInvoice.invoiceId)"
              >
                {{ nearestPayoutInvoice.invoiceId }}
              </CButton>
              <small
                v-else
                class="text-muted"
              >N/A</small>
            </CWidgetSimple>
          </CCol>
          <CCol
            col="6"
            lg="3"
            class="px-2 mb-2"
          >
            <CWidgetSimple
              header="Highest Payout"
              :text="formatAmount(highestAmountInvoice.totalInvoicedAmount)"
            >
              <CButton
                v-if="highestAmountInvoice.invoiceId"
                size="sm"
                color="link"
                class="p-0"
                @click="getDownloadLink(highestAmountInvoice.accountId, highestAmountInvoice.invoiceId)"
              >
                {{ highestAmountInvoice.invoiceId }}
              </CButton>
              <small
                v-else
                class="text-muted"
              >N/A</small>
            </CWidgetSimple>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody>
        <div
          v-if="isLoading"
          :style="{ height: '80vh' }"
          class="d-flex flex-column justify-content-center align-items-center"
        >
          <CSpinner
            style="width: 4rem; height: 4rem"
            color="info"
            grow
          />
        </div>

        <div v-else>
          <div class="d-flex align-items-center mb-2">
            <CSwitch
              variant="3d"
              color="primary"
              horizontal
              size="sm"
              class="mr-2"
              :checked.sync="includeTestOnly"
            />
            <h5 class="m-1">
              Show Test-Only Accounts
            </h5>
          </div>

          <div
            v-if="accountType === 'buyer'"
            class="mb-3 d-flex justify-content-end"
          >
            <CButton
              color="success"
              size="sm"
              @click="showRegisterPaymentModal = true"
            >
              <CIcon
                name="cil-dollar"
                class="mr-1"
              />
              Register Payment
            </CButton>
          </div>



          <CDataTable
            responsive
            small
            fixed
            border
            sorter
            column-filter
            :items="filteredInvoices"
            :fields="fields"
            :items-per-page="10"
            :pagination="{ align: 'center' }"
            :column-filter-value.sync="columnFilterValues"
            :sorter-value.sync="sorterValue"
          >
            <template #paymentStatusId-filter>
              <select
                v-model="paymentStatusIdFilter"
                class="form-control form-control-sm"
              >
                <option :value="undefined" />
                <option value="Overdue">
                  Overdue
                </option>
                <option value="Outstanding">
                  Outstanding
                </option>
                <option value="Paid">
                  Paid
                </option>
              </select>
            </template>

            <template #billingCycle="{ item }">
              <td>
                {{
                  getSafe(
                    () =>
                      `${formatDate(item.billingCycleStartDate)} - ${formatDateInclusive(item.billingCycleEndDate)}`
                  )
                }}
              </td>
            </template>

            <template #dateDue="{ item }">
              <td>
                {{ getSafe(() => `${formatDateInclusive(item.dateDueRaw)}`) }}
              </td>
            </template>

            <template #paymentDate="{ item }">
              <td>
                {{
                  getSafe(() =>
                    item.paymentDate ? `${formatDate(item.paymentDate)}` : 'N/A'
                  )
                }}
              </td>
            </template>

            <template #totalInvoicedAmount="{ item }">
              <td>
                <div>
                  <strong>{{
                    getSafe(() => `${formatAmount(item.totalInvoicedAmount)}`)
                  }}</strong>
                </div>
                <div
                  v-if="item.amountPaid && item.amountPaid > 0"
                  class="text-success small"
                >
                  <CIcon
                    name="cil-check-circle"
                    size="sm"
                  />
                  {{ formatAmount(item.amountPaid) }} paid
                </div>
                <div
                  v-if="item.amountPaid && item.amountPaid > 0 && item.totalInvoicedAmount - item.amountPaid > 0"
                  class="text-muted small"
                >
                  {{ formatAmount(item.totalInvoicedAmount - item.amountPaid) }} remaining
                </div>
              </td>
            </template>

            <template #invoiceStatusId="{ item }">
              <td>
                <CButton
                  v-c-tooltip="getVerificationStatus(item) === 'success' ? 'Verification successful' : getVerificationStatus(item) === 'pending' ? 'Verification pending' : 'Verification failed'"
                  size="sm"
                  :color="getVerificationStatus(item) === 'success' ? 'success' : getVerificationStatus(item) === 'pending' ? 'info' : 'danger'"
                  variant="outline"
                  @click="showExtrasModalFunc(item)"
                >
                  <CIcon
                    :name="getVerificationStatus(item) === 'success' ? 'cil-check-circle' : getVerificationStatus(item) === 'pending' ? 'cil-clock' : 'cil-x-circle'"
                    class="mr-1"
                  />
                  {{ getVerificationStatus(item) === 'success' ? 'Success' : getVerificationStatus(item) === 'pending' ? 'Pending' : 'Failed' }}
                </CButton>
              </td>
            </template>

            <template #paymentMethod="{ item }">
              <td>
                {{ getSafe(() => `${item.paymentMethod || 'N/A'}`) }}
              </td>
            </template>

            <template #accountName="{ item }">
              <td>
                <div class="account-cell">
                  <router-link
                    class="account-name-link"
                    target="_blank"
                    :to="{
                      name:
                        accountType === 'buyer'
                          ? 'BuyerAccountDetails'
                          : 'SellerAccountDetails',
                      params: { id: item.accountId },
                    }"
                  >
                    {{ getSafe(() => `${item.accountName}`) }}
                    <CIcon
                      name="cil-external-link"
                      size="custom"
                      class="ml-1"
                    />
                  </router-link>
                  <div class="account-actions mt-1">
                    <CBadge
                      v-if="accountType === 'seller'"
                      color="info"
                      class="action-badge"
                      role="link"
                      :to="`/dashboard/accounts/seller/${item.accountId}/payout-metrics`"
                      target="_blank"
                      title="View Payout Metrics"
                    >
                      Payouts
                    </CBadge>
                    <CBadge
                      color="secondary"
                      class="action-badge"
                      role="link"
                      :to="`/dashboard/accounts/${accountType}/${item.accountId}/payments`"
                      target="_blank"
                      title="View Payment History"
                    >
                      Payments
                    </CBadge>
                  </div>
                </div>
              </td>
            </template>

            <template #paymentStatusId="{ item }">
              <td>
                <CBadge :color="getBadge(item.paymentStatusId)">
                  {{ item.paymentStatusId }}
                </CBadge>
              </td>
            </template>

            <template #invoiceId="{ item }">
              <td class="text-center">
                <CButton
                  size="sm"
                  color="info"
                  variant="outline"
                  @click="getDownloadLink(item.accountId, item.invoiceId)"
                >
                  {{ item.invoiceId }}

                  <CIcon
                    name="cilCloudDownload"
                    class="ml-1"
                  />
                </CButton>
              </td>
            </template>

            <template #Actions="{ item }">
              <td>
                <div 
                  v-if="item.paymentStatusId === 'Outstanding' || item.paymentStatusId === 'Overdue'"
                  class="d-flex"
                >
                  <CButton
                    v-if="item.paymentStatusId === 'Outstanding' || item.paymentStatusId === 'Overdue'"
                    color="info"
                    size="sm"
                    @click="showPayInvoiceModal(item)"
                  >
                    Register Payment
                  </CButton>
                </div>
              </td>
            </template>
          </CDataTable>
        </div>
      </CCardBody>
    </CCard>
    
    <!-- Login Modal START -->
    <CModal
      title="Please login again to verify its you"
      color="primary"
      data-cy="login-modal"
      :show.sync="showLoginModal"
    >
      <LoginInternal
        v-if="showLoginModal"
        @success="onLoginSuccess"
        @error="onLoginFailed"
      />

      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- Login Modal END -->

    <CModal
      :title="`Payment of ${formatAmount(invoiceDetails.totalInvoicedAmount)} for ${invoiceDetails.accountName}`"
      color="info"
      size="lg"
      :show.sync="showInvoiceDetails"
    >
      <CForm
        novalidate
        @submit.prevent
      >
        <CInput
          type="text"
          label="Billing Period"
          name="billingPeriod"
          :value="`${formatDate(invoiceDetails.billingCycleStartDate)} - ${formatDateInclusive(invoiceDetails.billingCycleEndDate)}`"
          horizontal
          plaintext
        />
        <CInput
          type="text"
          label="Invoice Amount"
          name="invoiceAmount"
          :value="formatAmount(invoiceDetails.totalInvoicedAmount)"
          horizontal
          plaintext
        />
        <CInput
          v-if="paymentDetails.amountPaid !== undefined && paymentDetails.amountPaid !== null"
          type="text"
          label="Amount Paid"
          name="amountPaid"
          :value="formatAmount(paymentDetails.amountPaid || 0)"
          horizontal
          plaintext
          :description="paymentDetails.amountPaid > 0 ? 'Total amount paid so far' : 'No payments made yet'"
        />
        <CInput
          v-if="remainingInvoiceAmount !== null && remainingInvoiceAmount !== invoiceDetails.totalInvoicedAmount"
          type="text"
          label="Remaining Amount"
          name="remainingAmount"
          :value="formatAmount(remainingInvoiceAmount)"
          horizontal
          plaintext
          description="Amount still unpaid after partial payments"
        />
        <CInput
          v-if="invoiceDetails.openingBalance"
          type="text"
          label="Opening Balance"
          name="openingBalance"
          :value="formatAmount(invoiceDetails.openingBalance)"
          horizontal
          plaintext
        />
        <CRow
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Remaining Amount</label>
          </CCol>
          <CCol sm="9">
            <div class="input-group currency-input-wrapper">
              <input
                v-model="paymentAmountInCents"
                v-money="moneyConfig"
                type="text"
                class="form-control currency-input"
                placeholder="0.00"
                :readonly="accountType === 'seller'"
                @input="onPaymentAmountChange"
              >
            </div>
            <small
              v-if="accountType === 'buyer'"
              class="form-text text-muted"
            >Amount still unpaid on this invoice (partial payments allowed)</small>
            <small
              v-if="accountType === 'seller'"
              class="form-text text-muted"
            >Full invoice amount (sellers must pay invoices in full)</small>
          </CCol>
        </CRow>
        <CRow
          v-if="accountType === 'buyer'"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Apply Cascade</label>
          </CCol>
          <CCol>
            <CSwitch
              variant="3d"
              color="primary"
              :checked.sync="applyCascade"
              :disabled="!canApplyCascade"
              class="mr-2"
            />
            <small class="text-muted">Apply this payment across multiple invoices if amount exceeds this invoice</small>
          </CCol>
        </CRow>
        <CRow
          form
          class="form-group"
        >
          <CCol sm="3">
            View Invoice
          </CCol>
          <CCol>
            <CButton
              size="sm"
              color="info"
              variant="outline"
              @click="getDownloadLink(invoiceDetails.accountId, invoiceDetails.invoiceId)"
            >
              {{ invoiceDetails.invoiceId }}
            </CButton>
          </CCol>
        </CRow>

        <!-- Payment Information -->
        <CTextarea
          v-if="invoiceDetails.paymentStatusId === 'Outstanding' || invoiceDetails.paymentStatusId === 'Overdue'"
          id="notes"
          v-model="notes"
          label="Description"
          placeholder="Enter payment details"
          horizontal
          rows="3"
        />

        <!-- Previous Payment History (for partial payments) -->
        <div v-if="paymentDetails.paymentHistory && paymentDetails.paymentHistory.length > 0">
          <hr class="my-3">
          <h6 class="mb-3">
            Previous Payments
          </h6>
          <div
            v-for="(payment, index) in paymentDetails.paymentHistory"
            :key="index"
            class="mb-3 p-3 bg-light rounded"
          >
            <CInput
              label="Amount Paid"
              :value="formatAmount(payment.amountPaid)"
              horizontal
              plaintext
            />
            <CInput
              v-if="payment.description"
              label="Description"
              :value="payment.description"
              horizontal
              plaintext
            />
            <CInput
              v-if="payment.paidOn"
              label="Paid On"
              :value="formatDate(payment.paidOn)"
              horizontal
              plaintext
            />
            <CInput
              v-if="payment.user && payment.user.name"
              label="Paid By"
              :value="payment.user.name"
              horizontal
              plaintext
            />
          </div>
        </div>
      </CForm>

      <template #footer>
        <ButtonLoading
          v-if="invoiceDetails.paymentStatusId === 'Outstanding' || invoiceDetails.paymentStatusId === 'Overdue'"
          color="primary"
          :loading="isLoading"
          :disabled="!canSubmitInvoicePayment"
          @click="onConfirmSubmit"
        >
          Submit Payment
        </ButtonLoading>
        <div v-if="invoiceDetails.paymentStatusId !== 'Outstanding' && invoiceDetails.paymentStatusId !== 'Overdue'" />
      </template>
    </CModal>

    <!-- Invoice Verification Modal -->
    <InvoiceVerificationModal
      :show.sync="showExtrasModal"
      :loading="loadingInvoicePreview"
      :invoice-details="invoiceDetails"
      :invoice-preview-data="invoicePreviewData"
      :payment-details="paymentDetails"
      @run-verification="runVerification(invoiceDetails)"
      @regenerate-invoice="regenerateInvoiceHandler(invoiceDetails)"
      @delete-invoice="deleteInvoiceHandler(invoiceDetails)"
      @issue-invoice="markInvoiceAsIssuedHandler(invoiceDetails)"
    />

    <!-- Register Payment Modal -->
    <CModal
      :show.sync="showRegisterPaymentModal"
      :close-on-backdrop="false"
      size="lg"
      title="Register Payment"
      @update:show="value => !value && closeRegisterPaymentModal()"
    >
      <CForm>
        <CRow
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Buyer Account</label>
          </CCol>
          <CCol sm="9">
            <v-select
              v-model="registerPayment.accountId"
              :options="distinctBuyerAccounts"
              :reduce="option => option.value"
              label="label"
              placeholder="Search by account name or ID..."
              :filterable="true"
              :class="{ 'is-invalid': !registerPayment.accountId && registerPayment.isLoading }"
            >
              <template #no-options>
                No buyer accounts found
              </template>
            </v-select>
            <small
              v-if="!registerPayment.accountId && showAccountValidation"
              class="form-text text-danger"
            >
              Please select a buyer account
            </small>
          </CCol>
        </CRow>

        <CRow
          v-if="calculatedOutstandingBalance !== null"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Outstanding Balance</label>
          </CCol>
          <CCol>
            <strong class="text-danger">{{ formatAmount(calculatedOutstandingBalance) }}</strong>
            <small class="text-muted d-block">Total amount due from all unpaid invoices</small>
          </CCol>
        </CRow>

        <CRow
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Payment Amount</label>
          </CCol>
          <CCol>
            <div class="input-group currency-input-wrapper">
              <input
                v-model="registerPayment.paymentAmountInCents"
                v-money="moneyConfig"
                type="text"
                :class="{
                  'form-control': true,
                  'currency-input': true,
                  'is-invalid': $v.registerPayment.paymentAmount.$dirty && $v.registerPayment.paymentAmount.$invalid
                }"
                placeholder="0.00"
                @input="onRegisterPaymentAmountChange"
                @blur="$v.registerPayment.paymentAmount.$touch()"
              >
            </div>
            <small
              v-if="$v.registerPayment.paymentAmount.$dirty && !$v.registerPayment.paymentAmount.required"
              class="form-text text-danger"
            >
              Payment amount is required
            </small>
            <small
              v-else-if="$v.registerPayment.paymentAmount.$dirty && !$v.registerPayment.paymentAmount.minValue"
              class="form-text text-danger"
            >
              Payment amount must be at least $0.01
            </small>
          </CCol>
        </CRow>

        <!-- Apply Cascade (Buyers Only) -->
        <CRow
          v-if="accountType !== 'seller'"
          form
          class="form-group"
        >
          <CCol sm="3">
            <label>Apply Cascade</label>
          </CCol>
          <CCol>
            <CSwitch
              variant="3d"
              color="primary"
              :checked.sync="registerPayment.applyCascade"
              class="mr-2"
            />
            <small class="text-muted">Apply this payment across multiple invoices if amount exceeds the oldest invoice</small>
          </CCol>
        </CRow>

        <CTextarea
          v-model="registerPayment.description"
          label="Description"
          placeholder="Enter payment description"
          horizontal
          rows="3"
          :class="{ 'is-invalid': $v.registerPayment.description.$dirty && $v.registerPayment.description.$invalid }"
          @blur="$v.registerPayment.description.$touch()"
        />
        <CInvalidFeedback
          v-if="$v.registerPayment.description.$dirty && !$v.registerPayment.description.required"
        >
          Description is required
        </CInvalidFeedback>
      </CForm>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="registerPayment.isLoading"
          :disabled="!canSubmitPayment"
          @click="onSubmitPayment"
        >
          Submit Payment
        </ButtonLoading>
        <CButton
          color="secondary"
          @click="closeRegisterPaymentModal"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <!-- Generic Confirmation Modal -->
    <CModal
      :title="confirmationTitle"
      :color="confirmationVariant"
      :show.sync="showConfirmationModal"
    >
      <div v-if="confirmationItem">
        <div v-if="confirmationAction === CONFIRMATION_TYPES.REGENERATE">
          Are you sure you want to regenerate invoice <strong>{{ confirmationItem.invoiceId }}</strong>?
          <br><br>
          <div class="alert alert-warning">
            <ul class="mb-0 pl-3">
              <li>The Invoice ID will <strong>remain the same</strong>.</li>
              <li>The system will <strong>recalculate</strong> line items and adjustment amounts.</li>
              <li>The invoice will <strong>NOT</strong> be resent to the customer.</li>
            </ul>
          </div>
        </div>
        <div v-else-if="confirmationAction === CONFIRMATION_TYPES.DELETE">
          Are you sure you want to delete invoice <strong>{{ confirmationItem.invoiceId }}</strong>? This cannot be undone.
          <br><br>
          <div class="alert alert-danger">
            <ul class="mb-0 pl-3">
              <li>The invoice will be <strong>removed</strong> from the system.</li>
              <li>A <strong>new invoice</strong> with a <strong>different Invoice ID</strong> will be created.</li>
              <li>The new invoice <strong>WILL be sent</strong> to the customer via email.</li>
            </ul>
          </div>
        </div>
        <div v-else-if="confirmationAction === CONFIRMATION_TYPES.MARK_AS_ISSUED">
          Are you sure you want to mark invoice <strong>{{ confirmationItem.invoiceId }}</strong> as Issued?
          <br><br>
          <div class="alert alert-info">
            This will manually set the invoice status to "Issued", bypassing standard verification.
          </div>
        </div>
      </div>
      <template #footer>
        <CButton
          :color="confirmationVariant"
          @click="handleConfirmation"
        >
          Confirm
        </CButton>
        <CButton
          color="secondary"
          @click="showConfirmationModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import debounce from 'lodash.debounce'
import { mapState, mapActions, mapGetters } from 'vuex'
import Vue from 'vue'
import { required, minValue } from 'vuelidate/lib/validators'

import InvoiceVerificationModal from '@/components/InvoiceVerificationModal.vue'
import booleanBadgeMixin from '@/mixins/boolean-badge-mixin'
import { formatDate, formatDateInclusive, formatDateTime, formatAmount } from '@/utils'
import LoginInternal from '../Login/LoginInternal.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import BuyerBillingCharts from './BuyerBillingCharts.vue'

const urlMapping = {
  paymentStatusIdFilter: 'paymentStatus',
  accountIdFilter: 'accountId',
  invoiceIdFilter: 'invoiceId',
  accountNameFilter: 'accountName',
  showActiveInvoices: 'active'
}

export default {
  name: 'AccountPayments',
  components: {
    LoginInternal,
    InvoiceVerificationModal,
    BuyerBillingCharts
  },
  mixins: [booleanBadgeMixin],

  props: {
    payments: {
      type: Object,
      default() {
        return {
          invoices: [],
          summary: {},
        }
      },
    },
    accountType: {
      type: String,
      default: '',
    },
    isLoading: Boolean,
  },

  data() {
    return {
      includeTestOnly: false,
      showActiveOnly: true,
      paymentStatusIdFilter: '',
      accountIdFilter: '',
      accountNameFilter: '',
      invoiceIdFilter: '',
      sorterValue: { column: 'dateDue', asc: false },
      fields: [
        {
          key: 'billingCycle',
          label: 'Billing Period',
          filter: true,
          sorter: true
        },
        { key: 'dateDue', label: 'Due', sortable: true, filter: false },
        {
          key: 'paymentStatusId',
          label: 'Status',
          sortable: true,
          filter: true,
        },
        { key: 'accountName', label: 'Account', sortable: true, filter: true },
        { key: 'totalInvoicedAmount', label: 'Amount', sortable: true, filter: true },
        { key: 'invoiceStatusId', label: 'Verified', sortable: true, filter: true },
        { key: 'invoiceId', label: 'Invoice #', filter: true, _classes: 'text-center' },
        { key: 'Actions', filter: false },
      ],
      showLoginModal: false,
      showInvoiceDetails: false,
      showExtrasModal: false,
      invoiceDetails: {},
      notes: '',
      paymentAmount: 0,
      paymentAmountInCents: 0,
      applyCascade: true,

      // Confirmation Modal State
      showConfirmationModal: false,
      confirmationAction: '',
      confirmationItem: null,
      columnFilterValuesState: {},
      invoicePreviewResponse: null,
      loadingInvoicePreview: false,
      showRegisterPaymentModal: false,
      showAccountValidation: false,
      registerPayment: {
        accountId: '',
        paymentAmount: null,
        paymentAmountInCents: null,
        applyCascade: true,
        description: '',
        isLoading: false
      },
      isProgrammaticUpdate: false,
      moneyConfig: {
        decimal: '.',
        thousands: ',',
        suffix: '',
        precision: 2,
        masked: false,
        allowBlank: false,
        prefix: '$ ',
        min: 0.01
      },
      enableFakeChartData: false,
      
      CONFIRMATION_TYPES: {
        REGENERATE: 'regenerate',
        DELETE: 'delete',
        MARK_AS_ISSUED: 'markAsIssued'
      }
    }
  },


  validations() {
    return {
      paymentAmount: {
        required,
        minValue: minValue(0.01)
      },
      registerPayment: {
        paymentAmount: {
          required,
          minValue: minValue(0.01)
        },
        description: {
          required
        }
      }
    }
  },

  url: urlMapping,

  computed: {
    ...mapState('billing', ['paymentDetails']),

    ...mapGetters('auth', ['isUserLoggedInWithinLastXMinutes']),

    paymentDetailsWithId() {
      const payment = this.paymentDetails || {}
      return {
        ...payment,
        id: payment.invoiceId //use invoiceId as id for ReviewNotes
      }
    },





    invoicePreviewData() {
      if (!this.invoicePreviewResponse) {
        return null
      }

      return {
        lineItems: this.invoicePreviewResponse.lineItems || [],
        adjustmentAmount: this.invoicePreviewResponse.adjustmentAmount || 0,
        disputeFeeAmount: this.invoicePreviewResponse.disputeFeeAmount || 0,
        startDate: this.invoicePreviewResponse.startDate,
        endDate: this.invoicePreviewResponse.endDate,
        errors: this.invoicePreviewResponse.errors || []
      }
    },

    invoiceTotal() {
      if (!this.invoicePreviewResponse || !this.invoicePreviewResponse.lineItems) {
        return 0
      }

      const lineItemsTotal = this.invoicePreviewResponse.lineItems.reduce((sum, item) => sum + (item.isDisplayOnly ? 0 : item.amount), 0)
      return lineItemsTotal +
             (this.invoicePreviewResponse.adjustmentAmount || 0) +
             (this.invoicePreviewResponse.disputeFeeAmount || 0)
    },

    columnFilterValues: {
      set: debounce(function (value) {
        this.columnFilterValuesState = value

        for (const columnName in value) {
          value[columnName] = value[columnName] || ''
          const dataPropName = Object.keys(urlMapping).find(k=> urlMapping[k] === columnName)

          if (!value[columnName] && !this[dataPropName]) {
            continue
          }
          
          if (dataPropName in this) {
            Vue.set(this, dataPropName, value[columnName])
          }
        }
      }, 500),

      get() {
        return {
          ...this.columnFilterValuesState,
          accountName: this.accountNameFilter || '',
          invoiceId: this.invoiceIdFilter || ''
        }
      }
    },

    isUserLoggedInWithinLast60Minutes() {
      return this.isUserLoggedInWithinLastXMinutes(60)
    },
    
    outstandingCount() {
      return this.filteredInvoices.filter(x => x.paymentStatusId === 'Outstanding').length
    },

    outstandingAmount() {
      const result = this.filteredInvoices
        .filter(x => x.paymentStatusId === 'Outstanding')
        .reduce((accumulator, currentItem) => accumulator + currentItem.totalInvoicedAmount, 0)
      return result
    },

    overdueCount() {
      if (!this.filteredInvoices || this.filteredInvoices.length === 0) {
        return 0
      }

      const result = this.filteredInvoices.filter(x => x.paymentStatusId === 'Overdue').length
      return result
    },

    overdueAmount() {
      if (!this.filteredInvoices || this.filteredInvoices.length === 0) {
        return 0
      }

      const result = this.filteredInvoices
        .filter(x => x.paymentStatusId === 'Overdue')
        .reduce((accumulator, currentItem) => accumulator + currentItem.totalInvoicedAmount, 0)
      return result ?? 0
    },

    nearestPayoutInvoice(){
      let dummyResult = { dateDueRaw: 'N/A' }
      if (!this.filteredInvoices || this.filteredInvoices.length === 0) {
        return dummyResult
      }

      const result = this.filteredInvoices.reduce((prev, current) => {
        if (!prev){
          return current
        }

        const result = prev.dateDue <= current.dateDue ? prev : current
        return result
      })
      return result ?? dummyResult
    },

    highestAmountInvoice(){
      let dummyResult = {}
      if (!this.filteredInvoices || this.filteredInvoices.length === 0) {
        return dummyResult
      }

      const result = this.filteredInvoices.reduce((prev, current) => {
        if (!prev){
          return current
        }

        const result = prev.totalInvoicedAmount > current.totalInvoicedAmount ? prev : current
        return result
      })      
      return result ?? dummyResult
    },

    filteredInvoices() {
      if (!this.payments || !this.payments.invoices || this.payments.invoices.length === 0) {
        return []
      }

      let result = this.payments.invoices
        .filter(x => x.testOnly == this.includeTestOnly)
        .map(x => ({
          ...x,
          billingCycle: x.billingCycleStartDate, // use billing cycle start date for sorter
          dateDueRaw: x.dateDue, // preserve original ISO string for display via moment.parseZone
          dateDue: x.dateDue ? new Date(x.dateDue).valueOf() : 0 // numeric timestamp so CDataTable sorts chronologically across mixed offsets
        }))

      if (this.paymentStatusIdFilter) {
        result = result.filter(x => x.paymentStatusId === this.paymentStatusIdFilter)
      }

      if (this.accountIdFilter) {
        result = result.filter(x => x.accountId === this.accountIdFilter)
      }

      if (this.showActiveOnly) {
        const overdueInvoices = result
          .filter(x => x.paymentStatusId === 'Overdue')
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)) || []

        const outstandingInvoices = result
          .filter(x => x.paymentStatusId === 'Outstanding') || []

        //bring the overdue invoices on top (sorted by the nearest due date)
        result = overdueInvoices.concat(outstandingInvoices)
      }
      
      return result
    },

    showActiveInvoices: {
      get() {
        return this.showActiveOnly ? 'yes' : 'no'
      },

      set(value) {
        this.showActiveOnly = value === 'yes'
      }
    },

    canSubmitPayment() {
      // Check if all required fields are valid
      const basicValidation = (
        this.registerPayment.accountId &&
        !this.$v.registerPayment.paymentAmount.$invalid &&
        !this.$v.registerPayment.description.$invalid &&
        !this.registerPayment.isLoading
      )
      return basicValidation
    },

    canSubmitInvoicePayment() {
      const amount = parseFloat(this.paymentAmount)
      return (
        !isNaN(amount) &&
        amount >= 0.01 &&
        !this.isLoading
      )
    },

    canApplyCascade() {
      // Cascade is only possible when payment amount exceeds the remaining invoice amount
      const amount = parseFloat(this.paymentAmount)
      const remaining = this.remainingInvoiceAmount || this.invoiceDetails.totalInvoicedAmount || 0
      return !isNaN(amount) && amount > remaining
    },

    distinctBuyerAccounts() {
      // Extract unique buyer accounts from loaded invoices that have unpaid balances
      if (!this.payments || !this.payments.invoices) {
        return []
      }

      const accountMap = new Map()

      // Only include buyers with Outstanding or Overdue invoices
      this.payments.invoices.forEach(invoice => {
        if (invoice.accountId &&
            !accountMap.has(invoice.accountId) &&
            (invoice.paymentStatusId === 'Outstanding' || invoice.paymentStatusId === 'Overdue')) {
          accountMap.set(invoice.accountId, {
            value: invoice.accountId,
            label: `${invoice.accountName || invoice.accountId} (${invoice.accountId})`
          })
        }
      })

      return Array.from(accountMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      )
    },

    calculatedOutstandingBalance() {
      // Calculate outstanding balance from loaded invoices instead of making API call
      if (!this.registerPayment.accountId || !this.payments || !this.payments.invoices) {
        return null
      }

      // Sum up remaining balance (totalInvoicedAmount - amountPaid) for Outstanding and Overdue invoices
      const outstandingBalance = this.payments.invoices
        .filter(invoice =>
          invoice.accountId === this.registerPayment.accountId &&
          (invoice.paymentStatusId === 'Outstanding' || invoice.paymentStatusId === 'Overdue')
        )
        .reduce((sum, invoice) => {
          const remaining = invoice.totalInvoicedAmount - (invoice.amountPaid || 0)
          return sum + remaining
        }, 0)

      return outstandingBalance
    },

    remainingInvoiceAmount() {
      if (!this.paymentDetails || !this.invoiceDetails.totalInvoicedAmount) {
        return null
      }

      // Use the total amount paid directly from paymentDetails
      const totalPaid = this.paymentDetails.amountPaid || 0

      // Calculate remaining: total invoiced amount already includes opening balance and adjustments
      // So we just subtract what's been paid
      const remaining = (this.invoiceDetails.totalInvoicedAmount || 0) - totalPaid

      return remaining > 0 ? remaining : 0
    },

    confirmationTitle() {
      const configs = {
        [this.CONFIRMATION_TYPES.REGENERATE]: 'Regenerate Invoice',
        [this.CONFIRMATION_TYPES.DELETE]: 'Delete Invoice',
        [this.CONFIRMATION_TYPES.MARK_AS_ISSUED]: 'Mark Invoice as Issued'
      }
      return configs[this.confirmationAction] || ''
    },

    confirmationVariant() {
      const configs = {
        [this.CONFIRMATION_TYPES.REGENERATE]: 'warning',
        [this.CONFIRMATION_TYPES.DELETE]: 'danger',
        [this.CONFIRMATION_TYPES.MARK_AS_ISSUED]: 'info'
      }
      return configs[this.confirmationAction] || 'primary'
    },



  },

  watch: {
    paymentStatusIdFilter: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue === 'Paid') {
          this.showActiveOnly = false
        }
      }
    },

    showActiveOnly: {
      immediate: true,
      handler(newValue) {
        if (newValue && this.paymentStatusIdFilter === 'Paid') {
          setTimeout(() => {
            this.paymentStatusIdFilter = undefined
          }, 0)
        }
      }
    },

    'registerPayment.accountId'(newValue) {
      // Clear account validation error when account is selected
      if (newValue) {
        this.showAccountValidation = false
      }
    },

    canApplyCascade: {
      handler(newValue) {
        // Automatically uncheck cascade when payment amount is less than invoice amount
        if (!newValue && this.applyCascade) {
          this.applyCascade = false
        }
      }
    }
  },

  methods: {
    ...mapActions('billing', [
      'generateDownloadLink',
      'getPaymentDetails',
      'registerBuyerPayment',
      'registerSellerPayout',
      'runInvoiceVerification',
      'previewInvoice',
      'getAccountOutstandingBalance',
      'registerGenericPayment',
      'regenerateInvoice',
      'deleteInvoice',
      'markInvoiceAsIssued'
    ]),

    ...mapActions('auth', ['logout']),



    getSafe(fn) {
      try {
        return fn()
      } catch (error) {
        return 'N/A'
      }
    },

    getPaymentAmountError() {
      // Don't show errors during programmatic updates
      if (this.isProgrammaticUpdate || !this.$v.paymentAmount.$dirty) {
        return ''
      }

      if (!this.$v.paymentAmount.required) {
        return 'Payment amount is required'
      }

      if (!this.$v.paymentAmount.minValue) {
        return 'Payment amount must be greater than 0'
      }

      return ''
    },

    getBadge(status) {
      switch (status) {
        case 'Outstanding':
          return 'primary'

        case 'Paid':
          return 'success'

        case 'PartiallyPaid':
          return 'secondary'

        case 'Overdue':
          return 'danger'

        default:
          return 'warning'
      }
    },

    resetForm() {
      this.invoiceDetails = {}
      this.notes = ''
      this.paymentAmount = null
      this.paymentAmountInCents = null
      this.applyCascade = true
      this.$v.$reset()
    },

    isVerificationSuccessful(item) {
      return item.invoiceStatusId === 'Issued'
    },

    getVerificationStatus(item) {
      // Returns: 'success', 'pending', or 'failed'
      const status = item.invoiceStatusId

      // Success states
      if (status === 'Issued' || status === 'VerificationSucceeded') {
        return 'success'
      }

      // Failed states
      if (status === 'GeneratePdfFailed' || status === 'VerificationFailed') {
        return 'failed'
      }

      // Pending states (GeneratePdfPending, VerificationPending, or anything else)
      return 'pending'
    },

    async runVerification(item) {
      try {
        await this.runInvoiceVerification({
          accountId: item.accountId,
          invoiceId: item.invoiceId
        })
        showSuccessMessage('Verification checks have been restarted. Please refresh to see updated results.')
        this.$emit('reload-payments')
      } catch (error) {
        showErrorMessage(error)
      }
    },

    async submitInvoicePayment() {
      // Only touch the validation for the per-invoice payment, not all validations
      this.$v.paymentAmount.$touch()

      // Only check the per-invoice payment validation, not registerPayment validation
      if (this.$v.paymentAmount.$invalid) {
        // Debug: Log validation state
        console.log('Invoice payment validation failed:', {
          paymentAmount: this.paymentAmount,
          notes: this.notes,
          validation: {
            paymentAmount: {
              invalid: this.$v.paymentAmount.$invalid,
              required: this.$v.paymentAmount.required,
              minValue: this.$v.paymentAmount.minValue
            }
          }
        })

        const errors = []
        if (!this.$v.paymentAmount.required) {
          errors.push('Payment amount is required')
        } else if (!this.$v.paymentAmount.minValue) {
          errors.push('Payment amount must be at least $0.01')
        }

        const errorMessage = errors.length > 0
          ? `Validation errors: ${errors.join(', ')}`
          : 'Please fix validation errors before submitting'

        showErrorMessage(errorMessage)
        return
      }

      this.showLoginModal = false
      this.showInvoiceDetails = false

      try {
        const paymentData = {
          accountId: this.invoiceDetails.accountId,
          invoiceId: this.invoiceDetails.invoiceId,
          data: {
            description: this.notes,
            paymentAmount: this.paymentAmount,
            applyCascade: this.applyCascade
          }
        }

        // Call appropriate API method based on account type
        if (this.accountType === 'buyer') {
          await this.registerBuyerPayment(paymentData)
        } else if (this.accountType === 'seller') {
          await this.registerSellerPayout(paymentData)
        } else {
          throw new Error('Invalid account type')
        }

        showSuccessMessage('The invoice has been successfully paid.')
        this.$emit('reload-payments')
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.resetForm()
      }
    },

    onLoginSuccess() {
      if (this.confirmationItem && this.confirmationAction) {
        this.executeConfirmationAction()
        return
      }

      this.submitInvoicePayment()
    },


    onLoginFailed() {
      this.onLogout()
    },

    onLogout() {
      try {
        this.logout()
      } catch (error) {
        //
      } finally {
        this.$router.push({ name: 'LoginEmail' })
      }
    },

    async showPayInvoiceModal(item) {
      await this.getPaymentDetails({accountId: item.accountId, invoiceId: item.invoiceId})

      this.invoiceDetails = item

      // Prefill with remaining amount (what still needs to be paid after partial payments)
      // remainingInvoiceAmount is calculated from: totalInvoicedAmount - sum of all payments made
      let amountInDollars = this.remainingInvoiceAmount

      // If remainingInvoiceAmount is null (payment details not loaded yet), calculate from invoice data
      // totalInvoicedAmount already includes opening balance and adjustments
      if (amountInDollars === null || amountInDollars === undefined) {
        const totalPaid = item.amountPaid || 0
        amountInDollars = (item.totalInvoicedAmount || 0) - totalPaid
      }

      // Ensure amount is not negative
      amountInDollars = Math.max(0, amountInDollars)

      // Convert dollars to cents for v-money (multiply by 100)
      this.paymentAmount = amountInDollars
      this.paymentAmountInCents = Math.round(amountInDollars * 100)

      // Set cascade based on whether payment amount exceeds invoice amount
      // Since we're prefilling with the exact remaining amount, cascade should be false
      this.applyCascade = false

      // Reset validation state
      this.$v.$reset()

      // Show modal AFTER values are set
      this.showInvoiceDetails = true
    },

    async showExtrasModalFunc(item) {
      this.invoiceDetails = item
      this.showExtrasModal = true
      this.invoicePreviewResponse = null
      await Promise.all([
        this.getPaymentDetails({accountId: item.accountId, invoiceId: item.invoiceId}),
        this.loadInvoicePreview()
      ])
    },

    regenerateInvoiceHandler(item) {
      // If triggered from modal, modal is still open, so we need to close it or handle z-index.
      // Ideally we close the details modal first?
      // For now, let's keep details modal open if possible, but the confirmation modal will overlay it.
      this.showConfirmation(this.CONFIRMATION_TYPES.REGENERATE, item)
    },

    deleteInvoiceHandler(item) {
      this.showConfirmation(this.CONFIRMATION_TYPES.DELETE, item)
    },

    markInvoiceAsIssuedHandler(item) {
      this.showConfirmation(this.CONFIRMATION_TYPES.MARK_AS_ISSUED, item)
    },

    showConfirmation(actionType, item) {
      this.confirmationAction = actionType
      this.confirmationItem = item
      
      const messages = {
        [this.CONFIRMATION_TYPES.REGENERATE]: `
          Are you sure you want to regenerate invoice <strong>${item.invoiceId}</strong>?
          <br><br>
          <div class="alert alert-warning">
            <ul class="mb-0 pl-3">
              <li>The Invoice ID will <strong>remain the same</strong>.</li>
              <li>The system will <strong>recalculate</strong> line items and adjustment amounts.</li>
              <li>The invoice will <strong>NOT</strong> be resent to the customer.</li>
            </ul>
          </div>`,
        [this.CONFIRMATION_TYPES.DELETE]: `
          Are you sure you want to delete invoice <strong>${item.invoiceId}</strong>? This cannot be undone.
          <br><br>
          <div class="alert alert-danger">
            <ul class="mb-0 pl-3">
              <li>The invoice will be <strong>removed</strong> from the system.</li>
              <li>A <strong>new invoice</strong> with a <strong>different Invoice ID</strong> will be created.</li>
              <li>The new invoice <strong>WILL be sent</strong> to the customer via email.</li>
            </ul>
          </div>`,
        [this.CONFIRMATION_TYPES.MARK_AS_ISSUED]: `
          Are you sure you want to manually mark invoice <strong>${item.invoiceId}</strong> as <strong>Issued</strong>?
          <br><br>
          <div class="alert alert-info">
             This will bypass verification checks and finalize the invoice.
          </div>`
      }

      this.confirmationTitle = actionType === this.CONFIRMATION_TYPES.DELETE ? 'Delete Invoice' : 
        actionType === this.CONFIRMATION_TYPES.REGENERATE ? 'Regenerate Invoice' : 
          'Issue Invoice'
      
      this.confirmationMessage = messages[actionType]
      this.confirmationVariant = actionType === this.CONFIRMATION_TYPES.DELETE ? 'danger' : 
        actionType === this.CONFIRMATION_TYPES.REGENERATE ? 'warning' : 'primary'
      
      this.showConfirmationModal = true
    },

    async handleConfirmation() {
      this.showConfirmationModal = false

      if (!this.isUserLoggedInWithinLast60Minutes) {
        this.showLoginModal = true
        return
      }

      await this.executeConfirmationAction()
    },

    async executeConfirmationAction() {
      const item = this.confirmationItem

      const actionMap = {
        [this.CONFIRMATION_TYPES.REGENERATE]: {
          action: () => this.regenerateInvoice({ accountId: item.accountId, invoiceId: item.invoiceId }),
          successMessage: 'Invoice regeneration started'
        },
        [this.CONFIRMATION_TYPES.DELETE]: {
          action: () => this.deleteInvoice({ accountId: item.accountId, invoiceId: item.invoiceId }),
          successMessage: 'Invoice deleted'
        },
        [this.CONFIRMATION_TYPES.MARK_AS_ISSUED]: {
          action: () => this.markInvoiceAsIssued({ accountId: item.accountId, invoiceId: item.invoiceId }),
          successMessage: 'Invoice marked as issued'
        }
      }

      const config = actionMap[this.confirmationAction]
      if (!config) {
        console.error('Unknown confirmation action:', this.confirmationAction)
        return
      }

      try {
        await config.action()
        showSuccessMessage(config.successMessage)
        this.$emit('reload-payments')
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.confirmationItem = null
        this.confirmationAction = ''
      }
    },

    async loadInvoicePreview() {
      this.loadingInvoicePreview = true
      this.invoicePreviewResponse = null

      try {
        const response = await this.previewInvoice({
          accountId: this.invoiceDetails.accountId,
          startDate: this.invoiceDetails.billingCycleStartDate,
          endDate: this.invoiceDetails.billingCycleEndDate,
          invoiceId: this.invoiceDetails.invoiceId
        })
        this.invoicePreviewResponse = response
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.loadingInvoicePreview = false
      }
    },

    async onConfirmSubmit() {
      
      if (!this.isUserLoggedInWithinLast60Minutes) {
        this.showInvoiceDetails = false
        this.showLoginModal = true
        return
      }
      
      await this.submitInvoicePayment()
    },

    async getDownloadLink(accountId, invoiceId) {
      const downloadURL = await this.generateDownloadLink({
        accountId,
        invoiceId,
      })
      window.open(downloadURL)
    },

    formatCheckDate(check) {
      const start = check.startDate ? this.formatDateTime(check.startDate) : 'N/A'
      return  start
    },

    hasFieldError(field) {
      if (!this.invoicePreviewData?.errors) {
        return false
      }

      return this.invoicePreviewData.errors.some(error => error.field === field)
    },

    getFieldError(field) {
      if (!this.invoicePreviewData?.errors) {
        return null
      }

      const error = this.invoicePreviewData.errors.find(error => error.field === field)
      return error ? error.error : null
    },

    hasLineItemError(index, field) {
      if (!this.invoicePreviewData?.errors) {
        return false
      }

      const fieldName = `LineItems[${index}].${field}`
      return this.invoicePreviewData.errors.some(error => error.field === fieldName)
    },

    getLineItemError(index, field) {
      if (!this.invoicePreviewData?.errors) {
        return null
      }

      const fieldName = `LineItems[${index}].${field}`
      const error = this.invoicePreviewData.errors.find(error => error.field === fieldName)
      return error ? error.error : null
    },

    // Outstanding balance is now calculated from loaded invoice data via computed property

    async onSubmitPayment() {
      // Validate all fields
      this.$v.registerPayment.$touch()

      // Check if account is selected
      if (!this.registerPayment.accountId) {
        this.showAccountValidation = true
      }

      if (this.$v.registerPayment.$invalid || !this.registerPayment.accountId) {
        const errors = []

        // Check account selection
        if (!this.registerPayment.accountId) {
          errors.push('Buyer account must be selected')
        }

        // Check payment amount
        if (this.$v.registerPayment.paymentAmount.$invalid) {
          if (!this.$v.registerPayment.paymentAmount.required) {
            errors.push('Payment amount is required')
          } else if (!this.$v.registerPayment.paymentAmount.minValue) {
            errors.push('Payment amount must be at least $0.01')
          }
        }

        // Check description
        if (this.$v.registerPayment.description.$invalid) {
          errors.push('Description is required')
        }

        const errorMessage = errors.length > 0
          ? `Validation errors: ${errors.join(', ')}`
          : 'Please fix validation errors before submitting'

        showErrorMessage(errorMessage)
        return
      }

      // Clear account validation flag if we got here
      this.showAccountValidation = false

      if (!this.isUserLoggedInWithinLast60Minutes) {
        this.showLoginModal = true
        this.showRegisterPaymentModal = false
        return
      }

      this.registerPayment.isLoading = true

      try {
        // Register payment for buyer account
        await this.registerGenericPayment({
          accountId: this.registerPayment.accountId,
          paymentAmount: this.registerPayment.paymentAmount,
          applyCascade: this.registerPayment.applyCascade,
          description: this.registerPayment.description
        })

        showSuccessMessage('Payment has been successfully registered.')
        this.closeRegisterPaymentModal()
        this.$emit('reload-payments')
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.registerPayment.isLoading = false
      }
    },

    closeRegisterPaymentModal() {
      this.showRegisterPaymentModal = false
      this.showAccountValidation = false
      this.registerPayment = {
        accountId: '',
        paymentAmount: null,
        paymentAmountInCents: null,
        applyCascade: true,
        description: '',
        isLoading: false
      }
      // Reset validation
      this.$v.registerPayment.$reset()
    },

    onPaymentAmountChange() {
      // Convert cents to dollars for validation and submission
      if (this.paymentAmountInCents) {
        // v-money may format the value as a string (e.g., "$ 2,011.00")
        // We need to parse it back to a number
        let valueInCents
        if (typeof this.paymentAmountInCents === 'string') {
          // Remove currency symbols, spaces, and thousands separators, then parse
          const cleaned = this.paymentAmountInCents.replace(/[$,\s]/g, '')
          valueInCents = parseFloat(cleaned) * 100 // Convert dollars to cents
        } else {
          valueInCents = this.paymentAmountInCents
        }

        this.paymentAmount = valueInCents / 100

        // Only touch validation if this is a user interaction, not programmatic update
        if (!this.isProgrammaticUpdate) {
          this.$v.paymentAmount.$touch()
        }
      } else {
        this.paymentAmount = null
      }
    },

    onRegisterPaymentAmountChange() {
      // Convert cents to dollars for validation and submission
      if (this.registerPayment.paymentAmountInCents) {
        // v-money may format the value as a string (e.g., "$ 2,011.00")
        // We need to parse it back to a number
        let valueInCents
        if (typeof this.registerPayment.paymentAmountInCents === 'string') {
          // Remove currency symbols, spaces, and thousands separators, then parse
          const cleaned = this.registerPayment.paymentAmountInCents.replace(/[$,\s]/g, '')
          valueInCents = parseFloat(cleaned) * 100 // Convert dollars to cents
        } else {
          valueInCents = this.registerPayment.paymentAmountInCents
        }

        this.registerPayment.paymentAmount = valueInCents / 100

        // Touch validation to trigger it
        this.$v.registerPayment.paymentAmount.$touch()
      } else {
        this.registerPayment.paymentAmount = null
      }
    },

    formatDate,
    formatDateInclusive,
    formatDateTime,
    formatAmount,
  },
}
</script>

<style scoped>
/* Hide the ReviewNotes component header since we have our own styled header */
::v-deep .review-notes .card-header {
  display: none;
}

/* Error highlighting for verification errors */
.verification-error {
  background-color: #fff5f5 !important;
  border: 2px solid #e74c3c !important;
}

.error-field {
  color: #c0392b;
  font-weight: bold;
}

/* Currency input styling */
.currency-input-wrapper {
  max-width: 300px;
}

.currency-input {
  width: 100%;
}

.account-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-name-link {
  word-break: break-all;
}

.account-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-badge {
  cursor: pointer;
  font-size: 0.75rem;
  padding: 4px 8px;
}
</style>
